import React from "react";
import { type CalendarProps, DateLocalizer } from "react-big-calendar";
// @ts-ignore
import message from "react-big-calendar/lib/utils/messages";
import { NavLink, type NavLinkProps } from "react-router-dom";

import { faEllipsisV, faSignalStream } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { memoize } from "lodash";
import moment from "moment";
import { commitMutation } from "relay-runtime";

import { confirm } from "~/lib/prompts";
import { Twix } from "~/lib/twix";
import { hexToRGB, inCalendarMode } from "~/lib/utils";
import { RecordIconSwitchEventWrapper } from "~/meetingRecorder/RecordIcon";
import { environment } from "~/models";
import type { Calendar, DayEvent } from "~/nav/panels/Meetings";
import { HideEventMutation, invalidateHiddenEvent } from "~/nav/panels/Meetings/Events";
import { MeetingInfoModalWrapper } from "~/nav/panels/Meetings/MeetingInfoModal";
import { ConnectedIcon } from "~/stream/ConnectedIcon";
import Popup from "~/ui/legacy/Popup";

import MeetingInfoButton from "./MeetingInfoButton";
import { timeBlockTypeIcons } from "./utils";

import type { Events_HideEventMutation as HideEventMutationQuery } from "~/__generated__/Events_HideEventMutation.graphql";
import type { ResponseStatusChoices } from "~/__generated__/MeetingsPanelAttendanceMutation.graphql";

import agendaStyles from "~/nav/panels/Meetings/MeetingsPanel.global.module.css";

declare module "csstype" {
	interface Properties {
		"--navItemColor-rgb"?: string;
		"--navItemColor"?: string;
	}
}

interface AgendaViewDayEventProps extends CalendarProps<DayEvent> {
	components: CalendarProps<DayEvent>["components"] & {
		time: React.ComponentClass<{ event: DayEvent; day: Date; label: string }>;
	};
	event: DayEvent;
	day: Date;
	selected?: string;
	onSelectEvent: (e: DayEvent) => void;
	refreshEvents: () => void;
	localizer: DateLocalizer;
	openEditModal: (guid: string) => void;
	activeCalendar: Calendar;
	answerEventAttendance: (
		calendar: Calendar,
		eventGuid: string,
		userResponseStatus: ResponseStatusChoices,
		callback?: () => void,
	) => void;
	uxSimplificationEnabled: boolean;
}

interface AgendaViewDayEventState {
	modalOpen: boolean;
}

// Lodash's memoize accepts a resolver function as the second parameter
// That you can use to create a custom caching key.
// If you do not add that Lodash will use the first parameter of your memoized function
// As the caching key, which in this case would be the start date
// which will make it so that all meetings with the same start date
// uses the same memoized label (which will probably have the wrong end date).
// So we are using the start, end and hideDate to create our own custom key here.
const formatAllDay = memoize(
	(start: string | undefined, end: string | undefined) => {
		return new Twix(moment(start), moment(end).subtract(1, "seconds")).format({
			hideTime: true,
		});
	},
	(start: string | undefined, end: string | undefined) => (start ? start : "") + (end ? end : ""),
);
const formatNonAllDay = memoize(
	(start: string | undefined, end: string | undefined, hideDate: boolean) => {
		return new Twix(moment(start), moment(end)).format({
			hideDate: hideDate,
		});
	},
	(start: string | undefined, end: string | undefined, hideDate: boolean) =>
		(start ? start : "") + (end ? end : "") + (hideDate ? "1" : "0"),
);

export class AgendaViewDayEvent extends React.Component<AgendaViewDayEventProps, AgendaViewDayEventState> {
	state: AgendaViewDayEventState = {
		modalOpen: false,
	};

	messages: { [key: string]: string };

	constructor(props: AgendaViewDayEventProps) {
		super(props);
		this.messages = message(props.messages);
	}

	onOpenMeetingInfoModal = () => this.setState({ modalOpen: true });

	onCloseMeetingInfoModal = () => this.setState({ modalOpen: false });

	editEvent = () => {
		this.props.openEditModal(this.props.event.guid);
	};

	handleAnswerEventAttendance = (responseStatus: ResponseStatusChoices) => () => {
		const { activeCalendar, event, answerEventAttendance } = this.props;
		answerEventAttendance(activeCalendar, event.guid, responseStatus);
	};

	hideEventPressed = () => {
		this.onCloseMeetingInfoModal();
		this.props.refreshEvents();
	};

	onSelectEvent = () => {
		this.props.onSelectEvent(this.props.event);
	};

	hideEvent = () => {
		const { event } = this.props;
		const message = event.isHidden ? (
			"Showing a recurring event will also show all events in the series."
		) : (
			<>
				<span>
					You can reveal this event by selecting <i>Show hidden meetings</i> in the filters menu&ensp;
					<FontAwesomeIcon icon={faEllipsisV} size="1x" />
					&ensp;at the top of the meetings panel.
				</span>
				<br />
				<br />
				<span>Hiding a recurring event will also hide all events in the series.</span>
			</>
		);

		confirm(message, {
			yesText: event.isHidden ? "Show" : "Hide",
			title: event.isHidden ? "Show selected event again?" : "Hide selected event?",
		}).then(result => {
			if (!result) {
				return;
			}

			commitMutation<HideEventMutationQuery>(environment, {
				mutation: HideEventMutation,
				variables: { guid: this.props.event.guid },
				onCompleted: ({ hideEvent }) => {
					if (hideEvent?.ok) {
						invalidateHiddenEvent(this.props.event.id);
					}
				},
			});
		});
	};

	isActive: NavLinkProps["isActive"] = (match, _location): boolean => {
		if (Boolean(match)) {
			return true;
		}
		if (!window.location.pathname.startsWith("/stream/") || !this.props.event.note?.stream.id) {
			return false;
		}
		const { id } = this.props.event.note.stream;
		return window.location.pathname.includes(id);
	};

	timeout1?: number = undefined;
	timeout2?: number = undefined;

	componentWillUnmount() {
		clearTimeout(this.timeout1);
		clearTimeout(this.timeout2);
	}
	render() {
		const {
			title,
			start,
			end,
			guid,
			hasNoteContent,
			hasUnreadNoteContent,
			status,
			userResponseStatus,
			noteFinished,
			timeBlockType,
		} = this.props.event;

		const startDate = start && new Date(start);
		const endDate = end && new Date(end);
		const now = new Date();
		const inProgress = !!(startDate && endDate && startDate < now && now < endDate && !noteFinished);
		if (startDate && endDate) {
			// TODO: We have to clean setTimeout on unmount. This needs to be better if this component
			// does not refactored.
			// Re-render when meeting ends
			if (inProgress && !this.timeout1) {
				this.timeout1 = window.setTimeout(this.forceUpdate.bind(this), endDate.valueOf() - now.valueOf());
			}
			// Re-render when meeting starts
			if (!this.timeout2 && startDate && new Date() < startDate) {
				this.timeout2 = window.setTimeout(this.forceUpdate.bind(this), startDate.valueOf() - now.valueOf());
			}
		}

		const colorProps: { style?: React.CSSProperties } = {};
		if (this.props.event.color) {
			colorProps.style = {};
			colorProps.style["--navItemColor-rgb"] = hexToRGB(this.props.event.color);
			colorProps.style["--navItemColor"] = this.props.event.color;
		}

		return (
			<>
				<div
					data-tour-interaction-target="event-in-panel"
					className={cx("rbc-event", `status-${userResponseStatus}`, {
						"has-note": hasNoteContent,
						"is-cancelled": status === "cancelled",
					})}
					{...colorProps}
				>
					<MeetingInfoButton
						event={this.props.event}
						onHide={this.hideEvent}
						onEdit={this.editEvent}
						onOpenMeetingInfoModal={this.onOpenMeetingInfoModal}
						handleAnswerEventAttendance={this.handleAnswerEventAttendance}
					/>
					<NavLink
						onClick={this.onSelectEvent}
						// eslint-disable-next-line max-len
						to={`/${this.props.uxSimplificationEnabled && inCalendarMode ? "companion-mode/event" : "meetings"}/${guid}/`}
						className="rbc-event-inner"
						activeClassName="rbc-selected"
						isActive={this.isActive}
					>
						<div className={agendaStyles.meetingLink}>
							<div className="rbc-agenda-event-cell" data-test-id="rbc-agenda-event-cell">
								{this.props.event.note?.stream.hasExternalUsersOrHasRelatedExternalStreams && (
									<ConnectedIcon style={{ marginRight: "0.25rem" }} />
								)}
								{(timeBlockType == "focusTime" || timeBlockType == "outOfOffice") && (
									<FontAwesomeIcon icon={timeBlockTypeIcons[timeBlockType]} style={{ marginRight: "0.25em" }} />
								)}
								<p className={agendaStyles.eventTitle}>{title}</p>
								<div className={agendaStyles.indicatorsContainer}>
									<RecordIconSwitchEventWrapper event={this.props.event} className={agendaStyles.recordIconSwitch} />
									{hasNoteContent && (
										<span className={cx("rbc-agenda-note-dot", { [agendaStyles.unreadDot]: hasUnreadNoteContent })} />
									)}
								</div>
							</div>
							<div className="rbc-agenda-time-cell">{this.timeLabel(this.props.day, this.props.event, inProgress)}</div>
						</div>
					</NavLink>
					{this.state.modalOpen && (
						<MeetingInfoModalWrapper
							eventGuid={this.props.event.guid}
							onClose={this.onCloseMeetingInfoModal}
							hideEventPressed={this.hideEventPressed}
						/>
					)}
				</div>
			</>
		);
	}

	timeLabel(day: Date, event: DayEvent, inProgress: boolean) {
		const { components } = this.props;

		if (event.status === "cancelled") {
			return <span>Cancelled</span>;
		}

		const labelClass = [];
		const TimeComponent = components.time;
		let label = this.messages.allDay;

		const start = event.start || undefined;
		const end = event.end || undefined;

		const startsBeforeOrAtDay = !!start && new Date(start).setHours(0, 0, 0, 0) < new Date(day).setHours(0, 0, 0, 0);
		const endsAfterOrAtDay = !!end && new Date(end).getTime() >= new Date(day).setHours(23, 59, 59, 999);
		const startEndSameDay: boolean =
			!!start && !!end && new Date(start).setHours(0, 0, 0, 0) == new Date(end).setHours(0, 0, 0, 0);
		if (event.isAllDay && !startEndSameDay) {
			label = formatAllDay(start, end);
		}

		if (!event.isAllDay && !(startsBeforeOrAtDay && endsAfterOrAtDay)) {
			label = formatNonAllDay(start, end, startEndSameDay);
		}

		const continuesPrior = !!start && new Date(start).getTime() < new Date(day).setHours(0, 0, 0, 0);
		const continuesAfter =
			!!end && new Date(end).getTime() >= new Date(new Date().setDate(day.getDate() + 1)).setHours(0, 0, 0, 0);
		if (!(startEndSameDay && continuesPrior ? !continuesAfter : continuesAfter)) {
			if (continuesPrior) labelClass.push("rbc-continues-prior");
			if (continuesAfter) labelClass.push("rbc-continues-after");
		}

		if (inProgress) {
			labelClass.push("event-in-progress");
		}

		return (
			<span className={cx(labelClass)}>
				{inProgress && (
					<Popup
						inverted
						position="top center"
						size="tiny"
						trigger={<FontAwesomeIcon icon={faSignalStream} />}
						content="Meeting in progress"
					/>
				)}
				{TimeComponent ? <TimeComponent event={event} day={day} label={label} /> : label}
			</span>
		);
	}
}
